// Update lifecycle — add-on edition: is an installed `@vortex-os/*` ADD-ON behind
// its latest published version, and (the real trap) does the instance's version
// RANGE silently block the update? In 0.x, a caret pin caps the minor — `^0.7.0`
// means `>=0.7.0 <0.8.0`, so a published `0.9.0` is never picked up by `npm i`,
// and "I bumped it but nothing changed" follows. This READ-ONLY check surfaces it
// (in `vortex doctor` + the session-start report); it NEVER auto-bumps a pin.
//
// Reuses the base update-check machinery: `queryNpmLatest` (offline-silent, tight
// timeout, fail-fast, no injection surface) and `compareSemver`. Same safety
// envelope — any failure (offline, no npm, junk) yields no finding, never throws.

import { readFileSync } from "node:fs";
import { join } from "node:path";
import type { ModuleContext } from "@vortex-os/core";
import { queryNpmLatest, compareSemver } from "./update-check.js";

/** Base has its OWN dedicated check (`checkBaseUpdate`); exclude it here. */
const BASE = "@vortex-os/base";

export interface AddonStatus {
  readonly pkg: string;
  /** Installed version (node_modules/<pkg>/package.json), or null if not installed. */
  readonly installed: string | null;
  /** Latest published, or null when the registry check was skipped/failed/offline. */
  readonly latest: string | null;
  /** The version range declared in the instance package.json (e.g. "^0.7.0"). */
  readonly range: string;
  /** latest is strictly newer than installed. */
  readonly behind: boolean;
  /** behind AND the declared range does NOT admit latest (so `npm i` can't reach it).
   *  null = couldn't decide (unrecognized range form) — report "behind" without the claim. */
  readonly pinBlocks: boolean | null;
}

/** All `@vortex-os/*` add-on deps declared in the instance package.json (≠ base). */
function declaredAddons(repoRoot: string): Record<string, string> {
  try {
    const pj = JSON.parse(readFileSync(join(repoRoot, "package.json"), "utf8")) as {
      dependencies?: Record<string, string>;
      devDependencies?: Record<string, string>;
    };
    const all = { ...(pj.dependencies ?? {}), ...(pj.devDependencies ?? {}) };
    const out: Record<string, string> = {};
    for (const [name, range] of Object.entries(all)) {
      if (name.startsWith("@vortex-os/") && name !== BASE && typeof range === "string") out[name] = range;
    }
    return out;
  } catch {
    return {};
  }
}

function installedVersion(repoRoot: string, pkg: string): string | null {
  try {
    const m = JSON.parse(readFileSync(join(repoRoot, "node_modules", pkg, "package.json"), "utf8")) as {
      version?: unknown;
    };
    return typeof m.version === "string" ? m.version.trim() : null;
  } catch {
    return null;
  }
}

const CORE = /^(\d+)\.(\d+)\.(\d+)/;
function bump(v: string, idx: 0 | 1 | 2): string | null {
  const m = CORE.exec(v.trim().replace(/^[v^~]/, ""));
  if (!m) return null;
  const n = [Number(m[1]), Number(m[2]), Number(m[3])];
  n[idx]! += 1;
  for (let i = idx + 1; i < 3; i++) n[i] = 0;
  return n.join(".");
}

/**
 * Does the declared `range` admit `latest`? Handles the forms VortEX pins use —
 * caret (`^`, incl. the 0.x minor-cap trap), tilde (`~`), exact, and `*`/empty.
 * Anything else (`>=`, unions, urls) → null = "can't decide" (caller stays honest).
 */
export function rangeAllows(range: string, latest: string): boolean | null {
  const r = range.trim();
  if (r === "" || r === "*" || r === "latest" || r === "x") return true;
  const ge = (a: string, b: string) => {
    const c = compareSemver(a, b);
    return c === null ? null : c >= 0;
  };
  const lt = (a: string, b: string) => {
    const c = compareSemver(a, b);
    return c === null ? null : c < 0;
  };
  // Each form must match the WHOLE string (anchored) — so a compound range
  // (`^0.7.0 || ^0.9.0`, `>=0.7.0 <0.9.0`, `0.7.0 - 0.9.0`) does NOT get
  // misread by parsing only its prefix; anything not a single clean form falls
  // through to null = "can't decide" (so we never claim a false pinBlocks).
  const VER = String.raw`v?\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?`;
  // exact (a bare version, whole string). A value the loose VER regex admits but
  // compareSemver rejects (e.g. a malformed prerelease) → null, NOT false — an
  // unparseable pin must never read as a blocking pin.
  if (new RegExp("^" + VER + "$").test(r)) {
    const c = compareSemver(latest, r);
    return c === null ? null : c === 0;
  }
  // single caret
  if (new RegExp("^\\^" + VER + "$").test(r)) {
    const base = r.slice(1);
    const m = CORE.exec(base);
    if (!m) return null;
    const [maj, min] = [Number(m[1]), Number(m[2])];
    // caret cap: >=1.x → next major; 0.>0 → next minor; 0.0.z → next patch
    const upper = maj > 0 ? bump(base, 0) : min > 0 ? bump(base, 1) : bump(base, 2);
    if (!upper) return null;
    const lo = ge(latest, base);
    const hi = lt(latest, upper);
    return lo === null || hi === null ? null : lo && hi;
  }
  // single tilde
  if (new RegExp("^~" + VER + "$").test(r)) {
    const base = r.slice(1);
    const upper = bump(base, 1); // ~x.y.z → <x.(y+1).0
    if (!upper) return null;
    const lo = ge(latest, base);
    const hi = lt(latest, upper);
    return lo === null || hi === null ? null : lo && hi;
  }
  return null; // unions, comparators, hyphen-ranges, anything else → can't decide
}

/**
 * Check every declared add-on. Read-only + offline-silent: an add-on whose
 * registry lookup fails gets `latest: null` / `behind: false` and is simply not
 * a finding. The CALLER gates on `updates.check` (same as the base check), so a
 * user who turned update checks off triggers zero network here.
 */
/** Bound the per-session network cost — a pathological package.json can't make
 *  session start do unbounded serial `npm view` calls. Real instances have ≤ ~5
 *  `@vortex-os/*` add-ons, so this never truncates legitimate use. */
const MAX_ADDON_CHECKS = 8;

export function checkAddonUpdates(ctx: ModuleContext): AddonStatus[] {
  const declared = declaredAddons(ctx.repoRoot);
  const out: AddonStatus[] = [];
  const entries = Object.entries(declared)
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .slice(0, MAX_ADDON_CHECKS);
  for (const [pkg, range] of entries) {
    const installed = installedVersion(ctx.repoRoot, pkg);
    const latest = queryNpmLatest(ctx.repoRoot, pkg);
    const behind = compareSemver(latest ?? "", installed ?? "") === 1;
    out.push({
      pkg,
      installed,
      latest,
      range,
      behind,
      pinBlocks: behind ? rangeAllows(range, latest!) === false : null,
    });
  }
  return out;
}

/** Just the add-ons that are behind (the surfacing set). */
export function addonsBehind(statuses: AddonStatus[]): AddonStatus[] {
  return statuses.filter((s) => s.behind);
}
